import * as React from 'react';

import { API_CONSTANTS } from '../constants';
import { useBhanzuBuddyChat } from '../hooks/use-bhanzu-buddy-chat';

import { BhanzuBuddyChat } from './bhanzu-buddy-chat';
import type { BhanzuBuddyChatProps } from './bhanzu-buddy-chat/bhanzu-buddy-chat-types';

export interface BhanzuBuddyChatWithApiProps
  extends Omit<
    BhanzuBuddyChatProps,
    'messages' | 'onSendMessage' | 'onFeedback' | 'onGuideMe' | 'isLoading'
  > {
  leadId: string;
  source?: string;
  onMessageSent?: (response: any) => void;
  onError?: (error: any) => void;
}

/**
 * BhanzuBuddy Chat Component with API Integration
 *
 * This component wraps the existing BhanzuBuddyChat component and integrates
 * it with the BhanzuBuddy API for fetching chat history and sending messages.
 */
const BhanzuBuddyChatWithApi: React.FC<BhanzuBuddyChatWithApiProps> = ({
  leadId,
  source: initialSource = API_CONSTANTS.DEFAULT_SOURCE,
  onMessageSent,
  onError,
  onToggleMode,
  currentMode: controlledCurrentMode,
  className,
  ...props
}) => {
  const [internalCurrentMode, setInternalCurrentMode] = React.useState<
    'explain' | 'guide'
  >(controlledCurrentMode ?? 'explain');
  const [currentSource, setCurrentSource] = React.useState(initialSource);

  // Use controlled mode only if onToggleMode is provided (allowing external state management)
  const isControlled = onToggleMode !== undefined;
  const currentMode =
    isControlled && controlledCurrentMode !== undefined
      ? controlledCurrentMode
      : internalCurrentMode;

  // Update internal state when controlled value changes (for initial sync)
  React.useEffect(() => {
    if (controlledCurrentMode !== undefined && !isControlled) {
      setInternalCurrentMode(controlledCurrentMode);
    }
  }, [controlledCurrentMode, isControlled]);

  const handleToggleMode = React.useCallback(
    (mode: 'explain' | 'guide') => {
      if (!isControlled) {
        setInternalCurrentMode(mode);
      }
      // Update source based on mode
      const newSource =
        mode === 'explain'
          ? API_CONSTANTS.DEFAULT_SOURCE
          : API_CONSTANTS.GUIDE_SOURCE;
      setCurrentSource(newSource);

      onToggleMode?.(mode);
    },
    [isControlled, onToggleMode]
  );

  const { messages, isLoading, onSendMessage, onFeedback, onGuideMe } =
    useBhanzuBuddyChat({
      leadId,
      source: currentSource,
      onMessageSent,
      onError,
    });

  return (
    <BhanzuBuddyChat
      messages={messages}
      onSendMessage={onSendMessage}
      onFeedback={onFeedback}
      onGuideMe={onGuideMe}
      onToggleMode={handleToggleMode}
      currentMode={currentMode}
      isLoading={isLoading}
      className={className}
      {...props}
    />
  );
};

// Memoize the component to prevent unnecessary re-renders
const BhanzuBuddyChatWithApiMemo = React.memo(BhanzuBuddyChatWithApi);

export { BhanzuBuddyChatWithApiMemo as BhanzuBuddyChatWithApi };
